/**
 * CommentItem Component
 *
 * Story BL-024.2: Governance-Suite Comment Thread UI
 * Task 4: Create CommentItem component
 *
 * Renders a single comment with author info, vote controls, action buttons,
 * and nested replies. Handles tombstone state for deleted comments.
 *
 * @module components/governance/CommentItem
 */

import { useState } from 'react';
import { ChevronUp, ChevronDown, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import type { CommentThread } from '../../types/proposalComments';
import { CommentForm } from './CommentForm';
import { DeleteCommentModal } from './DeleteCommentModal';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format an ISO timestamp to a relative time string.
 * No external dependency (date-fns not in package.json).
 */
function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Check if a comment has been edited (updatedAt > createdAt + 60s).
 */
function isEdited(createdAt: string, updatedAt: string): boolean {
  return (
    new Date(updatedAt).getTime() > new Date(createdAt).getTime() + 60000
  );
}

// =============================================================================
// Types
// =============================================================================

export interface CommentItemProps {
  /** The comment (with optional replies for top-level threads) */
  comment: CommentThread;
  /** Current logged-in user ID (undefined if not authenticated) */
  currentUserId: string | undefined;
  /** Whether the current user is authenticated */
  isAuthenticated: boolean;
  /** Whether the current user is an admin or moderator */
  isModeratorOrAdmin: boolean;
  /** Callback when a vote button is clicked */
  onVote: (commentId: string, vote: 1 | -1) => void;
  /** Callback when the reply button is clicked */
  onReply: (commentId: string, content: string) => Promise<void>;
  /** Callback when the delete button is confirmed */
  onDelete: (commentId: string) => void;
  /** Callback when a comment is edited */
  onEdit: (commentId: string, content: string) => Promise<void>;
}

// =============================================================================
// Component
// =============================================================================

export function CommentItem({
  comment,
  currentUserId,
  isAuthenticated,
  isModeratorOrAdmin,
  onVote,
  onReply,
  onDelete,
  onEdit,
}: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isAuthor = currentUserId != null && comment.authorUserId === currentUserId;
  const isTopLevel = comment.parentId === null;

  // =========================================================================
  // Tombstone rendering for deleted comments
  // =========================================================================

  if (comment.isDeleted) {
    return (
      <div data-testid="comment-item-deleted" className="py-3">
        <span className="text-muted-foreground italic text-sm">[deleted]</span>
        {/* Still render replies for deleted top-level comments */}
        {isTopLevel && comment.replies && comment.replies.length > 0 && (
          <div className="pl-4 border-l border-border mt-2 space-y-2" data-testid="replies-section">
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={{ ...reply, replies: [] } as CommentThread}
                currentUserId={currentUserId}
                isAuthenticated={isAuthenticated}
                isModeratorOrAdmin={isModeratorOrAdmin}
                onVote={onVote}
                onReply={onReply}
                onDelete={onDelete}
                onEdit={onEdit}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // Action handlers
  // =========================================================================

  const handleDelete = () => {
    if (isModeratorOrAdmin && comment.authorUserId !== currentUserId) {
      // Use modal for moderator deletes on other users' comments (AC6)
      setShowDeleteModal(true);
    } else {
      // Author self-delete uses window.confirm
      if (window.confirm('Delete your comment? This cannot be undone.')) {
        onDelete(comment.id);
      }
    }
  };

  const handleReplySubmit = async (content: string) => {
    await onReply(comment.id, content);
    setShowReplyForm(false);
  };

  const handleEditSubmit = async (content: string) => {
    await onEdit(comment.id, content);
    setShowEditForm(false);
  };

  // =========================================================================
  // Render
  // =========================================================================

  const showDeleteButton =
    (isAuthor || isModeratorOrAdmin) && !comment.isDeleted;
  const showEditButton = isAuthor && !comment.isDeleted;

  return (
    <div data-testid="comment-item" className="py-3">
      {/* Author line */}
      <div className="flex items-center gap-2 mb-1">
        <span className="font-medium text-sm" data-testid="comment-author">
          {comment.authorDisplayName}
        </span>
        {comment.isProposer && (
          <span
            className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium"
            data-testid="proposer-badge"
          >
            Proposer
          </span>
        )}
        <time
          className="text-xs text-muted-foreground"
          title={new Date(comment.createdAt).toLocaleString()}
          data-testid="comment-timestamp"
        >
          {formatRelativeTime(comment.createdAt)}
        </time>
        {isEdited(comment.createdAt, comment.updatedAt) && (
          <span className="text-xs text-muted-foreground" data-testid="edited-label">
            (edited)
          </span>
        )}
      </div>

      {/* Content / Edit form */}
      {showEditForm ? (
        <div className="mt-2" data-testid="edit-form-wrapper">
          <CommentForm
            onSubmit={handleEditSubmit}
            onCancel={() => setShowEditForm(false)}
            isReply
            placeholder="Edit your comment…"
            initialValue={comment.content}
          />
        </div>
      ) : (
        <p className="text-sm mb-2" data-testid="comment-content">
          {comment.content}
        </p>
      )}

      {/* Vote controls + action buttons */}
      <div className="flex items-center gap-3 text-sm">
        {/* Vote controls */}
        <div className="flex items-center gap-1" data-testid="vote-controls">
          <button
            type="button"
            onClick={() => onVote(comment.id, 1)}
            disabled={!isAuthenticated}
            title={isAuthenticated ? 'Upvote' : 'Sign in to vote'}
            className={`flex items-center gap-0.5 p-0.5 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${
              comment.userVote === 1
                ? 'text-primary'
                : 'text-muted-foreground'
            }`}
            data-testid="upvote-button"
          >
            <ChevronUp className="h-4 w-4" />
            <span className="text-xs" data-testid="upvote-count">{comment.upvotes}</span>
          </button>
          <span
            className="text-xs font-medium min-w-[1.5rem] text-center"
            data-testid="vote-score"
          >
            {comment.score}
          </span>
          <button
            type="button"
            onClick={() => onVote(comment.id, -1)}
            disabled={!isAuthenticated}
            title={isAuthenticated ? 'Downvote' : 'Sign in to vote'}
            className={`flex items-center gap-0.5 p-0.5 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${
              comment.userVote === -1
                ? 'text-destructive'
                : 'text-muted-foreground'
            }`}
            data-testid="downvote-button"
          >
            <ChevronDown className="h-4 w-4" />
            <span className="text-xs" data-testid="downvote-count">{comment.downvotes}</span>
          </button>
        </div>

        {/* Reply button (top-level only, non-deleted, authenticated) */}
        {isTopLevel && isAuthenticated && (
          <button
            type="button"
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
            data-testid="reply-button"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Reply
          </button>
        )}

        {/* Edit button (author only) */}
        {showEditButton && (
          <button
            type="button"
            onClick={() => setShowEditForm(!showEditForm)}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
            data-testid="edit-button"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        )}

        {/* Delete button (author or moderator/admin) */}
        {showDeleteButton && (
          <button
            type="button"
            onClick={handleDelete}
            className="flex items-center gap-1 text-muted-foreground hover:text-destructive"
            data-testid="delete-button"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        )}
      </div>

      {/* Inline reply form */}
      {showReplyForm && (
        <div className="mt-2" data-testid="inline-reply-form">
          <CommentForm
            onSubmit={handleReplySubmit}
            onCancel={() => setShowReplyForm(false)}
            isReply
            placeholder="Write a reply…"
          />
        </div>
      )}

      {/* Replies section (top-level only) */}
      {isTopLevel && comment.replies && comment.replies.length > 0 && (
        <div className="pl-4 border-l border-border mt-2 space-y-2" data-testid="replies-section">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={{ ...reply, replies: [] } as CommentThread}
              currentUserId={currentUserId}
              isAuthenticated={isAuthenticated}
              isModeratorOrAdmin={isModeratorOrAdmin}
              onVote={onVote}
              onReply={onReply}
              onDelete={onDelete}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation modal (BL-024.3 AC6) */}
      <DeleteCommentModal
        visible={showDeleteModal}
        authorDisplayName={comment.authorDisplayName}
        isModerating={isModeratorOrAdmin && comment.authorUserId !== currentUserId}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          setShowDeleteModal(false);
          onDelete(comment.id);
        }}
      />
    </div>
  );
}

export default CommentItem;
